import { Briefcase, Link2, Pencil, Pin, Trash2 } from "lucide-react";
import {
  useEffect,
  useRef,
  useState,
  type FC,
  type KeyboardEvent
} from "react";

import type { ForeignNationalNote } from "@/api/foreignNationals";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tag } from "@/components/ui/tag";
import { AgentMoodAvatar } from "@/features/agents/components/AgentMoodAvatar";
import { cn } from "@/lib/utils";

import {
  authorName,
  bodyBlocks,
  highlightRuns,
  initialsOf,
  inlineRuns,
  isEdited,
  noteDateLabel
} from "./notesModel";

const Highlighted: FC<{ text: string; query: string; bold: boolean }> = ({
  text,
  query,
  bold
}) => (
  <>
    {highlightRuns(text, query).map((run, i) =>
      run.hit ? (
        <mark
          key={i}
          className={cn(
            "rounded-xs bg-warn-soft text-inherit",
            bold && "font-medium"
          )}
        >
          {run.text}
        </mark>
      ) : (
        <span key={i} className={bold ? "font-medium" : undefined}>
          {run.text}
        </span>
      )
    )}
  </>
);

const Line: FC<{ text: string; query: string }> = ({ text, query }) => (
  <>
    {inlineRuns(text).map((run, i) => (
      <Highlighted key={i} text={run.text} query={query} bold={run.bold} />
    ))}
  </>
);

/** A note's text: paragraphs, `- ` lists and `**bold**`, with search hits marked. */
export const NoteBody: FC<{ body: string; query?: string }> = ({
  body,
  query = ""
}) => (
  <div className="flex flex-col gap-0.5 text-md text-fg">
    {bodyBlocks(body).map((block, i) =>
      block.list ? (
        <ul key={i} className="list-disc pl-4.5">
          {block.items.map((item, j) => (
            <li key={j}>
              <Line text={item} query={query} />
            </li>
          ))}
        </ul>
      ) : (
        <p key={i} className="break-words whitespace-pre-wrap">
          <Line text={block.items[0]} query={query} />
        </p>
      )
    )}
  </div>
);

export const NoteAvatar: FC<{
  name: string;
  assistant?: boolean;
  image?: string;
  size?: "sm" | "md";
}> = ({ name, assistant, image, size = "md" }) =>
  assistant ? (
    <AgentMoodAvatar
      agent={{ key: "assistant" }}
      className={cn("rounded-full", size === "sm" ? "size-6" : "size-7")}
    />
  ) : (
    <Avatar size={size} shape="circle">
      {image ? <AvatarImage src={image} alt="" /> : null}
      <AvatarFallback className="text-sm">{initialsOf(name)}</AvatarFallback>
    </Avatar>
  );

/** "Suggested by the assistant · added by Maya Rivera", or the import's origin. */
const provenance = (note: ForeignNationalNote): string | null => {
  if (note.source === "assistant")
    return note.approved_by_name
      ? `Suggested by the assistant · added by ${note.approved_by_name}`
      : "Suggested by the assistant";
  if (note.source === "import") return "Imported from previous notes";
  return null;
};

type Props = {
  note: ForeignNationalNote;
  query: string;
  /** The caller wrote it: Edit and Delete are theirs. */
  own: boolean;
  avatar?: string;
  caseLabel?: string | null;
  highlighted?: boolean;
  editing: boolean;
  maxChars: number;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (body: string) => Promise<void> | void;
  onTogglePin: () => void;
  onCopyLink: () => void;
  onDelete: () => void;
};

export const NoteEntry: FC<Props> = ({
  note,
  query,
  own,
  avatar,
  caseLabel,
  highlighted,
  editing,
  maxChars,
  onEdit,
  onCancel,
  onSave,
  onTogglePin,
  onCopyLink,
  onDelete
}) => {
  const [draft, setDraft] = useState(note.body);
  const [saving, setSaving] = useState(false);
  const editRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (editing) setDraft(note.body);
  }, [editing, note.body]);
  useEffect(() => {
    if (editing) editRef.current?.focus();
  }, [editing]);

  const name = authorName(note);
  const origin = provenance(note);
  const tooLong = draft.length > maxChars;
  const canSave = Boolean(draft.trim()) && !tooLong && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(draft.trim());
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      void save();
    } else if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
    }
  };

  return (
    <article
      id={`note-${note.id}`}
      data-testid="note-entry"
      className={cn(
        "group/note flex gap-3 rounded-lg px-2.5 py-3 transition-colors duration-300",
        !editing && "hover:bg-fill-1",
        highlighted && "bg-fill-2"
      )}
    >
      <NoteAvatar
        name={name}
        assistant={note.source === "assistant"}
        image={avatar}
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex min-h-5 items-center gap-1.5">
          <span className="truncate text-base font-medium text-fg">{name}</span>
          <span className="shrink-0 text-sm whitespace-nowrap text-fg-3">
            {noteDateLabel(note.created_at)}
            {isEdited(note) ? " · Edited" : ""}
          </span>
          {note.pinned ? (
            <Pin className="size-3 shrink-0 text-fg-3" aria-label="Pinned" />
          ) : null}
          {!editing ? (
            <div className="-my-0.5 ml-auto flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-focus-within/note:opacity-100 group-hover/note:opacity-100">
              {own ? (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Edit"
                  title="Edit"
                  onClick={onEdit}
                >
                  <Pencil />
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={note.pinned ? "Unpin" : "Pin"}
                title={note.pinned ? "Unpin" : "Pin"}
                onClick={onTogglePin}
              >
                <Pin />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Copy Link"
                title="Copy Link"
                onClick={onCopyLink}
              >
                <Link2 />
              </Button>
              {own ? (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Delete"
                  title="Delete"
                  onClick={onDelete}
                >
                  <Trash2 />
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        {editing ? (
          <div className="flex flex-col gap-2">
            <textarea
              aria-label="Edit Note"
              data-testid="note-edit-text"
              ref={editRef}
              value={draft}
              rows={3}
              aria-invalid={tooLong || undefined}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              className="field-sizing-content min-h-16 w-full resize-none rounded-md border border-primary bg-transparent px-2 py-1.5 text-md text-fg outline-none aria-invalid:border-danger-line"
            />
            <div className="flex items-center gap-1.5">
              <span className="text-sm text-fg-4">
                ⌘↵ to save · Esc to cancel
              </span>
              <Button
                variant="ghost"
                size="xs"
                className="ml-auto"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                size="xs"
                disabled={!canSave}
                data-testid="note-edit-save"
                onClick={() => void save()}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <NoteBody body={note.body} query={query} />
        )}
        {caseLabel || origin ? (
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            {caseLabel ? (
              <Tag>
                <Briefcase />
                {caseLabel}
              </Tag>
            ) : null}
            {origin ? (
              <span className="text-sm text-fg-3">{origin}</span>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
};
